import { useState, useRef, useEffect } from 'react'
import { getPushToken } from '../services/push.js'

const CATEGORIES = ['Love', 'Work', 'Family', 'Friendship', 'Prayer', 'Other']
const MAX_LENGTH = 500
const API = import.meta.env.VITE_API_URL || ''

export default function NewConfessionModal({ onClose, onSuccess }) {
  const [text, setText]         = useState('')
  const [category, setCategory] = useState('')
  const [wantsPastor, setWantsPastor] = useState(false)
  const [error, setError]       = useState(null)
  const [loading, setLoading]   = useState(false)
  const textareaRef = useRef(null)

  useEffect(() => { textareaRef.current?.focus() }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    if (text.trim().length < 5) return setError('At least 5 characters required.')
    if (!category)              return setError('Please select a category.')

    setLoading(true)
    try {
      const res = await fetch(`${API}/api/confessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim(), category, wants_pastor: wantsPastor, device_token: getPushToken() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Error submitting confession.')
      onSuccess(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="New Confession">
        <div className="modal-header">
          <h2>{category === 'Prayer' ? 'Your Prayer Request' : 'Your Confession'}</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <textarea
              ref={textareaRef}
              className="confession-textarea"
              value={text}
              onChange={e => setText(e.target.value.slice(0, MAX_LENGTH))}
              placeholder={category === 'Prayer'
                ? 'What should others pray for? Everything stays anonymous…'
                : "What's on your mind? Everything stays anonymous…"}
              rows={6}
              maxLength={MAX_LENGTH}
              disabled={loading}
            />
            <div className={`char-count ${text.length >= MAX_LENGTH ? 'at-limit' : ''}`}>
              {text.length} / {MAX_LENGTH}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Category</label>
            <div className="category-grid">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  className={`category-option ${category === cat ? 'selected' : ''}`}
                  onClick={() => setCategory(cat)}
                  disabled={loading || (wantsPastor && cat !== 'Prayer')}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="pastor-option">
              <input
                type="checkbox"
                checked={wantsPastor}
                onChange={e => {
                  setWantsPastor(e.target.checked)
                  // Bitte um Seelsorge gehört immer in die Kategorie Prayer
                  if (e.target.checked) setCategory('Prayer')
                }}
                disabled={loading}
              />
              <span>
                ✝ I'd like a response from a pastor
                <small>A verified church representative will reply to your post – you stay anonymous.</small>
              </span>
            </label>
          </div>

          {error && (
            <div className="form-error" role="alert">{error}</div>
          )}

          <div className="modal-footer">
            <p className="anonymity-note">
              🔒 Fully anonymous · No account needed
            </p>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || text.trim().length < 5}
            >
              {loading ? 'Submitting…' : 'Confess'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
